import { useState, useEffect, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  NewspaperIcon,
  XMarkIcon,
  FunnelIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { supabase, getTournaments } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

interface PressArticleRow {
  id: string;
  tournament_id: string | null;
  source: string;
  title: string;
  date: string | null;
  quote: string;
  link: string;
  image_url: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ArticleFormData {
  tournament_id: string;
  source: string;
  title: string;
  date: string;
  quote: string;
  link: string;
  image_url: string;
}

const emptyForm: ArticleFormData = {
  tournament_id: '',
  source: '',
  title: '',
  date: '',
  quote: '',
  link: '',
  image_url: '',
};

interface Tournament {
  id: string;
  name: string;
  start_date: string;
}

interface PressCoverageManagerProps {
  tournamentId?: string;
}

export default function PressCoverageManager({ tournamentId }: PressCoverageManagerProps) {
  const [articles, setArticles] = useState<PressArticleRow[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filterTournamentId, setFilterTournamentId] = useState<string>(tournamentId || '');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<PressArticleRow | null>(null);
  const [deletingArticle, setDeletingArticle] = useState<PressArticleRow | null>(null);
  const [form, setForm] = useState<ArticleFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(!tournamentId);

  useEffect(() => {
    if (!tournamentId) {
      fetchTournaments();
    }
    fetchArticles();
  }, [filterTournamentId, showAll]);

  const fetchTournaments = async () => {
    try {
      const { data } = await getTournaments();
      if (data) setTournaments(data);
    } catch {
      // silent
    }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('press_articles')
        .select('*')
        .order('date', { ascending: false, nullsFirst: false });

      if (tournamentId) {
        query = query.eq('tournament_id', tournamentId);
      } else if (!showAll && filterTournamentId) {
        query = query.eq('tournament_id', filterTournamentId);
      } else if (!showAll && !filterTournamentId) {
        query = query.is('tournament_id', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      setArticles(data || []);
    } catch (err) {
      console.error('Error fetching press articles:', err);
      toast.error('Failed to load press articles');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingArticle(null);
    setForm({
      ...emptyForm,
      tournament_id: tournamentId || (filterTournamentId && !showAll ? filterTournamentId : ''),
    });
    setIsModalOpen(true);
  };

  const openEdit = (article: PressArticleRow) => {
    setEditingArticle(article);
    setForm({
      tournament_id: article.tournament_id || '',
      source: article.source,
      title: article.title,
      date: article.date || '',
      quote: article.quote,
      link: article.link,
      image_url: article.image_url || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        tournament_id: form.tournament_id || null,
        source: form.source,
        title: form.title,
        date: form.date || null,
        quote: form.quote,
        link: form.link,
        image_url: form.image_url || null,
        updated_at: new Date().toISOString(),
      };

      if (editingArticle) {
        const { error } = await supabase
          .from('press_articles')
          .update(payload)
          .eq('id', editingArticle.id);
        if (error) throw error;
        toast.success('Article updated');
      } else {
        const { error } = await supabase
          .from('press_articles')
          .insert([payload]);
        if (error) throw error;
        toast.success('Article added');
      }

      await fetchArticles();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving article:', err);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (article: PressArticleRow) => {
    setDeletingArticle(article);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingArticle) return;
    try {
      const { error } = await supabase
        .from('press_articles')
        .delete()
        .eq('id', deletingArticle.id);
      if (error) throw error;
      await fetchArticles();
      toast.success('Article deleted');
    } catch {
      toast.error('Failed to delete article');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingArticle(null);
    }
  };

  const getTournamentName = (id: string | null) => {
    if (!id) return null;
    return tournaments.find(t => t.id === id)?.name || null;
  };

  const formatDisplayDate = (date: string | null) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return date;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-golf-gray">Press Coverage Articles</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {tournamentId
              ? 'Press articles associated with this tournament.'
              : 'Manage press coverage articles. Associate them with tournaments for tournament-specific display.'}
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors text-sm font-medium shrink-0"
        >
          <PlusIcon className="w-4 h-4" />
          Add Article
        </button>
      </div>

      {!tournamentId && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FunnelIcon className="w-4 h-4" />
            <span>Show:</span>
          </div>
          <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1">
            <button
              onClick={() => { setShowAll(true); setFilterTournamentId(''); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                showAll ? 'bg-white shadow-sm text-golf-gray' : 'text-gray-500 hover:text-golf-gray'
              }`}
            >
              All Tournaments
            </button>
            <button
              onClick={() => { setShowAll(false); setFilterTournamentId(''); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                !showAll && !filterTournamentId ? 'bg-white shadow-sm text-golf-gray' : 'text-gray-500 hover:text-golf-gray'
              }`}
            >
              Unassociated
            </button>
          </div>
          {!showAll && (
            <select
              value={filterTournamentId}
              onChange={(e) => setFilterTournamentId(e.target.value)}
              className="border border-gray-300 rounded-lg text-sm focus:ring-golf-orange focus:border-golf-orange py-1.5 pl-3 pr-8"
            >
              <option value="">No specific tournament</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          {showAll && (
            <select
              value={filterTournamentId}
              onChange={(e) => { setFilterTournamentId(e.target.value); if (e.target.value) setShowAll(false); }}
              className="border border-gray-300 rounded-lg text-sm focus:ring-golf-orange focus:border-golf-orange py-1.5 pl-3 pr-8"
            >
              <option value="">Filter by tournament...</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {articles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <NewspaperIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No press articles yet</p>
          <p className="text-sm text-gray-400 mt-1">Click "Add Article" to add your first press coverage article.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map((article, index) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.04 }}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:border-golf-orange/40 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-golf-orange">{article.source}</span>
                    {article.date && (
                      <span className="text-xs text-gray-400">{formatDisplayDate(article.date)}</span>
                    )}
                    {article.tournament_id ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-golf-green/10 text-golf-green px-2 py-0.5 rounded-full">
                        <TrophyIcon className="w-3 h-3" />
                        {getTournamentName(article.tournament_id) || 'Tournament'}
                      </span>
                    ) : (
                      !tournamentId && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                          No tournament
                        </span>
                      )
                    )}
                  </div>
                  <p className="text-sm font-medium text-golf-gray truncate">{article.title}</p>
                  {article.quote && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 italic">"{article.quote}"</p>
                  )}
                  {article.link && (
                    <a
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline mt-1 block truncate"
                    >
                      {article.link}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(article)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(article)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Transition.Root show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title className="text-lg font-semibold text-golf-gray">
                      {editingArticle ? 'Edit Article' : 'Add Press Article'}
                    </Dialog.Title>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {!tournamentId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tournament (optional)</label>
                        <select
                          value={form.tournament_id}
                          onChange={(e) => setForm({ ...form, tournament_id: e.target.value })}
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                        >
                          <option value="">No specific tournament (general coverage)</option>
                          {tournaments.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                        <input
                          type="text"
                          required
                          value={form.source}
                          onChange={(e) => setForm({ ...form, source: e.target.value })}
                          placeholder="e.g. BBC Sport"
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <input
                          type="date"
                          value={form.date}
                          onChange={(e) => setForm({ ...form, date: e.target.value })}
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        required
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Article headline"
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Featured Quote</label>
                      <textarea
                        required
                        value={form.quote}
                        onChange={(e) => setForm({ ...form, quote: e.target.value })}
                        placeholder="A key quote from the article"
                        rows={3}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Article Link</label>
                      <input
                        type="url"
                        required
                        value={form.link}
                        onChange={(e) => setForm({ ...form, link: e.target.value })}
                        placeholder="https://..."
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                      <input
                        type="url"
                        value={form.image_url}
                        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                        placeholder="https://..."
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : editingArticle ? 'Save Changes' : 'Add Article'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeletingArticle(null); }}
        onConfirm={confirmDelete}
        title={deletingArticle?.title || ''}
        message="Are you sure you want to delete this press article? This action cannot be undone."
      />
    </div>
  );
}
